import { useEffect, useRef } from 'react';

import {
  useGetSellerWhatsNew,
  useGetSharedFunctionality,
} from '../../../api/lending/lapiHooks';
import { useUserGroups } from '../../../hooks/useUserGroups';

/**
 * Auto-opens the What's New pop-out once when an activated seller has unread
 * notifications.
 *
 * - Ref-guarded so it opens at most once per mount and never reopens after the
 *   user closes it (SellerPageTemplate persists across route changes).
 * - Gated on the `is_activated_seller` shared-functionality flag so a brand-new
 *   seller (no pay link yet) doesn't get the pop-out on their first visit.
 * - Suppressed for staff/impersonation: `POST read` is a no-op for staff, so
 *   without this it would reopen on every impersonated login.
 */
export const useAutoOpenWhatsNew = (setIsOpen: (isOpen: boolean) => void) => {
  const { unreadCount, isLoading } = useGetSellerWhatsNew();
  const { functionality, loadingFunctionality } = useGetSharedFunctionality({
    functionalities: ['is_activated_seller'],
  });
  const { isStaff, isLoading: loadingGroups } = useUserGroups();

  const hasAutoOpened = useRef(false);

  useEffect(() => {
    if (hasAutoOpened.current) return;
    if (isLoading || loadingFunctionality || loadingGroups) return;
    if (isStaff) return;
    if (!functionality?.is_activated_seller) return;
    if (unreadCount === 0) return;

    hasAutoOpened.current = true;
    setIsOpen(true);
  }, [
    unreadCount,
    isLoading,
    loadingFunctionality,
    loadingGroups,
    isStaff,
    functionality,
    setIsOpen,
  ]);
};
